import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';

// Constants copied for iframe context (since imports won't work in compiled script)
const SIGNALR_BRIDGE_MESSAGES = {
  CONNECT: 'CONNECT',
  SUBSCRIBE: 'SUBSCRIBE', 
  INVOKE: 'INVOKE',
  DISCONNECT: 'DISCONNECT',
  READY: 'READY',
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED', 
  RECONNECTING: 'RECONNECTING',
  MESSAGE: 'MESSAGE',
  INVOKE_RESULT: 'INVOKE_RESULT',
  INVOKE_ERROR: 'INVOKE_ERROR',
  ERROR: 'ERROR'
} as const;

class SignalRHandler {
  private connection: HubConnection | null = null;

  constructor() {
    this.setupMessageListener();
    this.notifyReady();
  }

  private setupMessageListener(): void {
    window.addEventListener('message', async (event: MessageEvent) => {
      const { type, data } = event.data;
      
      try {
        switch (type) {
          case SIGNALR_BRIDGE_MESSAGES.CONNECT:
            await this.handleConnect(data);
            break;
          case SIGNALR_BRIDGE_MESSAGES.SUBSCRIBE:
            this.handleSubscribe(data);
            break;
          case SIGNALR_BRIDGE_MESSAGES.INVOKE:
            await this.handleInvoke(data);
            break;
          case SIGNALR_BRIDGE_MESSAGES.DISCONNECT:
            await this.handleDisconnect();
            break;
        }
      } catch (error) {
        this.postToParent({
          type: SIGNALR_BRIDGE_MESSAGES.ERROR,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
  }

  private async handleConnect(data: { url: string; location: string; groupName: string }): Promise<void> {
    if (this.connection) {
      return;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(data.url, {
        headers: {
          'X-Sleekflow-Location': data.location,
        },
      })
      .withAutomaticReconnect()
      .build();

    // Setup connection event handlers
    this.connection.onclose(() => {
      this.postToParent({ type: SIGNALR_BRIDGE_MESSAGES.DISCONNECTED });
    });

    this.connection.onreconnecting(() => {
      this.postToParent({ type: SIGNALR_BRIDGE_MESSAGES.RECONNECTING });
    });

    this.connection.onreconnected(() => {
      this.postToParent({ type: SIGNALR_BRIDGE_MESSAGES.CONNECTED });
    });

    await this.connection.start();
    await this.connection.invoke('WebClientAddToGroup', data.groupName);
    
    this.postToParent({ type: SIGNALR_BRIDGE_MESSAGES.CONNECTED });
  }

  private handleSubscribe(data: { method: string }): void {
    if (!this.connection) {
      throw new Error('Connection not established');
    }

    this.connection.on(data.method, (...args: unknown[]) => {
      this.postToParent({
        type: SIGNALR_BRIDGE_MESSAGES.MESSAGE,
        method: data.method,
        args
      });
    });
  }

  private async handleInvoke(data: { method: string; args: unknown[]; id: string }): Promise<void> {
    if (!this.connection) {
      throw new Error('Connection not established');
    }

    try {
      const result = await this.connection.invoke(data.method, ...data.args);
      this.postToParent({
        type: SIGNALR_BRIDGE_MESSAGES.INVOKE_RESULT,
        id: data.id,
        result
      });
    } catch (error) {
      this.postToParent({
        type: SIGNALR_BRIDGE_MESSAGES.INVOKE_ERROR,
        id: data.id,
        error: error instanceof Error ? error.message : 'Invoke failed'
      });
    }
  }

  private async handleDisconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  private postToParent(message: unknown): void {
    window.parent.postMessage(message, '*');
  }

  private notifyReady(): void {
    this.postToParent({ type: SIGNALR_BRIDGE_MESSAGES.READY });
  }
}

// Initialize the handler
new SignalRHandler(); 